import { cn } from '@/lib/utils'
import { CSSProperties, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import BannerImage from 'app/frontend/base/components/BannerImage'
import Card from 'app/frontend/base/components/Card'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { usePageInfo } from 'app/frontend/base/config/queries'
import EntityLinksTile from './EntityLinksTile'
import { TextStyle } from 'app/frontend/types/custom/CustomDomainHeaderConfig'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'
import { useHasSidebar } from 'app/frontend/base/hooks/useHasSidebar'

const styleConfig: Record<
  Exclude<TextStyle, 'cards'> | 'noImage',
  { eyebrow: string; title: string; subtitle: string; style?: CSSProperties }
> = {
  textShadow: {
    eyebrow:
      'text-white/90 drop-shadow-[0_0_8px_rgba(0,0,0,0.8)] drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]',
    title:
      'text-white drop-shadow-[0_0_8px_rgba(0,0,0,0.8)] drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]',
    subtitle:
      'text-white/90 drop-shadow-[0_0_12px_rgba(0,0,0,1)] drop-shadow-[0_0_4px_rgba(0,0,0,1)] drop-shadow-[0_1px_2px_rgba(0,0,0,1)]',
  },
  scrim: {
    eyebrow: 'text-white/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]',
    title: 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]',
    subtitle: 'text-white/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]',
  },
  glass: {
    eyebrow: 'text-white/80 backdrop-blur-xs bg-black/20 rounded-lg py-1 px-3',
    title: 'text-white backdrop-blur-xs bg-black/20 rounded-lg py-2 px-3',
    subtitle: 'text-white/80 backdrop-blur-xs bg-black/20 rounded-lg py-2 px-3',
  },
  outline: {
    eyebrow: 'text-white/90 tracking-wide',
    title: 'text-white',
    subtitle: 'text-white/90 tracking-wide',
    style: {
      WebkitTextStroke: '4px rgba(0, 0, 0, 0.6)',
      paintOrder: 'stroke fill',
    },
  },
  noImage: {
    eyebrow: 'text-muted-foreground',
    title: '',
    subtitle: '',
  },
}

interface Props {
  title: string
  subtitle: string
  titleEyebrow?: string
  titleFooter?: ReactNode
  uriLinkFragment: string
  renderTiles?: (cardClassName: string) => ReactNode
}

const CustomDomainDashboardHeader = ({
  title,
  subtitle,
  titleEyebrow,
  titleFooter,
  uriLinkFragment,
  renderTiles,
}: Props) => {
  const { guild } = useGuild()
  const { customDomain } = useCustomDomain()
  const {
    data: { gameVersion },
  } = usePageInfo()
  const hasSidebar = useHasSidebar()
  const { pathname } = useLocation()

  const textStyle = customDomain?.headerConfig?.noImage
    ? 'noImage'
    : (customDomain?.headerConfig?.textStyle as TextStyle) ?? 'textShadow'

  const cardClassName = cn(
    customDomain?.headerConfig?.transparentCards
      ? 'bg-transparent border-none hover:bg-transparent'
      : '',
  )

  const backgroundImage = `url(${
    customDomain?.headerConfig?.backgroundImage ||
    `https://data.wowaudit.com/img/banners/${gameVersion}.jpg`
  })`

  const hasBodyBackground = !!(
    customDomain?.lightModeBodyBackgroundPath ||
    customDomain?.darkModeBodyBackgroundPath
  )

  const isFading =
    customDomain?.headerConfig?.homepageImageStyle === 'fading' &&
    pathname === '/' &&
    !customDomain?.headerConfig?.noImage &&
    !hasBodyBackground

  return (
    <header
      className={cn(
        '-mt-8 relative',
        isFading ? '' : 'border-b-2 bg-cover bg-center',
        customDomain?.headerConfig?.noImage ? 'bg-background' : '',
      )}
      style={{
        minHeight: `${customDomain?.headerConfig?.height ?? 160}px`,
        backgroundImage:
          customDomain?.headerConfig?.noImage || isFading
            ? undefined
            : backgroundImage,
      }}
    >
      {isFading && (
        <div className='pointer-events-none absolute inset-x-0 top-0 -z-10 h-[40rem] overflow-hidden'>
          <div
            className='h-full w-full bg-cover bg-center'
            style={{ backgroundImage }}
          />
          <div className='absolute inset-0 bg-linear-to-b from-transparent via-background/30 to-background' />
          <div className='absolute inset-0 bg-linear-to-r from-background/60 to-transparent' />
        </div>
      )}

      {textStyle === 'scrim' && !isFading && (
        <div className='absolute inset-0 bg-linear-to-r from-black/70 via-black/30 to-transparent' />
      )}

      <div
        className={cn(
          'relative z-10 flex justify-between items-center min-h-40 root-width py-4 gap-2 lg:gap-24 max-sm:flex-col max-sm:items-start',
          hasSidebar ? 'mx-0' : '',
        )}
      >
        <div className='flex items-center gap-2'>
          {customDomain?.headerConfig?.hideBanner ? null : (
            <BannerImage
              className='h-32 aspect-square max-sm:hidden'
              faction={guild?.faction}
              fileName={guild?.bannerFilename}
              size='custom'
            />
          )}

          <div className='flex flex-col gap-2 items-start'>
            {textStyle === 'cards' ? (
              <CardsText
                eyebrow={titleEyebrow}
                title={title}
                subtitle={subtitle}
                cardClassName={cardClassName}
              />
            ) : (
              <StyledText
                eyebrow={titleEyebrow}
                title={title}
                subtitle={subtitle}
                config={styleConfig[textStyle]}
              />
            )}
            {titleFooter}
          </div>
        </div>

        {renderTiles ? (
          renderTiles(cardClassName)
        ) : customDomain?.headerConfig?.hideLinks ? null : (
          <EntityLinksTile
            className={cardClassName}
            uriLinkFragment={uriLinkFragment}
          />
        )}
      </div>
    </header>
  )
}

const CardsText = ({
  eyebrow,
  title,
  subtitle,
  cardClassName,
}: {
  eyebrow?: string
  title: string
  subtitle: string
  cardClassName: string
}) => {
  return (
    <>
      {eyebrow ? (
        <Card className={cn(cardClassName, 'py-1 px-3')}>
          <span className='text-sm text-muted-foreground capitalize leading-6'>
            {eyebrow}
          </span>
        </Card>
      ) : null}

      {title ? (
        <Card className={cn(cardClassName, 'py-2 px-3')}>
          <h1 className='max-sm:text-xl leading-none'>{title}</h1>
        </Card>
      ) : null}

      {subtitle ? (
        <Card className={cn(cardClassName, 'py-2 px-3')}>
          <p className={cardClassName}>{subtitle}</p>
        </Card>
      ) : null}
    </>
  )
}

const StyledText = ({
  eyebrow,
  title,
  subtitle,
  config,
}: {
  eyebrow?: string
  title: string
  subtitle: string
  config: typeof styleConfig[keyof typeof styleConfig]
}) => (
  <>
    {eyebrow ? (
      <span
        className={cn('text-sm capitalize leading-6', config.eyebrow)}
        style={config.style}
      >
        {eyebrow}
      </span>
    ) : null}

    {title ? (
      <h1
        className={cn('max-sm:text-xl leading-none', config.title)}
        style={config.style}
      >
        {title}
      </h1>
    ) : null}

    {subtitle ? (
      <p className={config.subtitle} style={config.style}>
        {subtitle}
      </p>
    ) : null}
  </>
)

export default CustomDomainDashboardHeader
